// Analyse du classeur « RELANCES CLIENTS TOITURE » : le suivi des devis
// envoyés et de leur suite.
//
// Une feuille par année, une ligne par devis :
//   NOM | VILLE | TÉLÉPHONE | DEVIS (objet) | N° DEVIS | ENVOYÉ LE | MONTANT |
//   (statut, colonne sans titre) | COMMENTAIRES
//
// La colonne de statut n'a PAS d'en-tête dans le classeur : elle est repérée
// par sa position, juste après le montant. Les valeurs y sont saisies à la
// main (« Accepté », « Refusé », « En attente »), avec la casse et les accents
// que le clavier a bien voulu donner.
//
// Fonction PURE : aucun accès réseau ni base, donc testable ligne à ligne.

import { cellTexte, parseMontantCellule, versDateIso } from './extrabat.js';

export type StatutDevis = 'accepte' | 'refuse' | 'en_attente' | 'sans_reponse';

export interface DevisRelance {
  numero: string;
  client: string | null;
  ville: string | null;
  telephone: string | null;
  objet: string | null;
  date_envoi: string | null; // AAAA-MM-JJ
  montant: number;
  statut: StatutDevis;
  commentaire: string | null;
  annee: number;
}

/** Texte ramené à une forme comparable : sans accents, sans casse. */
function sansAccents(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** « Accepté », « ACCEPTE », « accepte » -> 'accepte'. Vide -> 'sans_reponse'. */
export function statutDepuisTexte(v: unknown): StatutDevis {
  const t = sansAccents(cellTexte(v));
  if (t === '') return 'sans_reponse';
  if (t.startsWith('accept')) return 'accepte';
  if (t.startsWith('refus')) return 'refuse';
  if (t.includes('attente')) return 'en_attente';
  // Tout autre mot est une note, pas un statut : on ne le prend pas pour une
  // réponse du client, sinon un devis à relancer sortirait du suivi.
  return 'sans_reponse';
}

/** Cellule vidée de ses blancs, ou null si elle n'apporte rien. */
function texteOuNull(v: unknown): string | null {
  const t = cellTexte(v).replace(/\s+/g, ' ').trim();
  return t === '' ? null : t;
}

/**
 * Numéro ramené à sa forme comparable avec celle du CRM.
 *
 * Le classeur écrit « DV0748 » là où le CRM écrit « DV0000916 » : même série,
 * deux largeurs. On compare sur le nombre, pas sur le texte.
 */
export function cleNumeroDevis(numero: string): string {
  const m = /^([A-Za-z]+)0*(\d+)$/.exec(numero.trim());
  if (!m?.[1] || !m[2]) return numero.trim().toUpperCase();
  return `${m[1].toUpperCase()}${m[2]}`;
}

/**
 * Analyse les feuilles du classeur de relances.
 *
 * `anneeDeFeuille` donne l'année de chaque feuille (son nom, « 2026 ») : les
 * feuilles sans année exploitable sont ignorées, de même que la feuille de
 * synthèse, qui ne porte pas de numéro de devis.
 */
export interface AnalyseRelances {
  devis: DevisRelance[];
  /** Dates trop éloignées de l'année de leur feuille, écartées comme fautives. */
  datesIgnorees: number;
  /** Devis figurant sur plusieurs feuilles, comptés une seule fois. */
  doublons: number;
}

export function analyserRelancesDevis(
  feuilles: { nom: string; lignes: unknown[][] }[],
): AnalyseRelances {
  const devis: DevisRelance[] = [];
  const vus = new Set<string>();
  let datesIgnorees = 0;
  let doublons = 0;

  for (const { nom, lignes } of feuilles) {
    const anneeFeuille = /^(\d{4})$/.exec(nom.trim())?.[1];
    if (!anneeFeuille) continue;

    for (const row of lignes) {
      const numero = cellTexte(row[4]).trim().toUpperCase();
      if (!/^DV\d/i.test(numero)) continue;
      // Un même devis peut figurer sur deux feuilles : la première lue gagne.
      const cle = cleNumeroDevis(numero);
      if (vus.has(cle)) {
        doublons += 1;
        continue;
      }
      vus.add(cle);

      // La date doit tomber près de l'année de sa feuille. Ce contrôle écarte
      // les deux fautes que le classeur contient réellement : le 1899-12-30
      // que rend le tableur pour une cellule vide, et les années tapées de
      // travers (un « 2202 » pour 2022). Mieux vaut aucune date qu'une fausse.
      const an = Number(anneeFeuille);
      const dateIso = versDateIso(row[5]);
      const anneeDate = dateIso ? Number(dateIso.slice(0, 4)) : null;
      const date = anneeDate !== null && Math.abs(anneeDate - an) <= 1 ? dateIso : null;
      if (dateIso && !date) datesIgnorees += 1;

      devis.push({
        numero,
        client: texteOuNull(row[0]),
        ville: texteOuNull(row[1]),
        telephone: texteOuNull(row[2]),
        objet: texteOuNull(row[3]),
        date_envoi: date,
        montant: parseMontantCellule(row[6]) ?? 0,
        statut: statutDepuisTexte(row[7]),
        commentaire: texteOuNull(row[8]),
        // L'année de la feuille prime : c'est le classement voulu par
        // l'utilisateur, et elle existe même quand la date manque.
        annee: Number(anneeFeuille),
      });
    }
  }
  return { devis, datesIgnorees, doublons };
}

// ----- Journal des appels ----------------------------------------------------
// Tenu dans le commentaire du devis, faute d'une table dédiée : une ligne datée
// par tentative. Cela suffit à ne pas rappeler indéfiniment les mêmes numéros,
// et l'historique reste lisible tel quel par un humain.

/** Préfixe des lignes écrites par l'écran d'appel. */
const MARQUE_APPEL = 'appel —';

/**
 * Ajoute une ligne datée au commentaire, sans effacer ce qui s'y trouve.
 *
 * La date porte l'ANNÉE : sans elle, un appel du 3 janvier ne se distinguerait
 * pas de celui de l'an dernier, et la pile d'appels se tromperait d'ordre.
 */
export function noterAppel(commentaire: string | null, issue: string, quand: Date): string {
  const jour = `${String(quand.getDate()).padStart(2, '0')}/${String(quand.getMonth() + 1).padStart(2, '0')}/${String(quand.getFullYear())}`;
  const ligne = `${jour} ${MARQUE_APPEL} ${issue}`;
  return commentaire && commentaire.trim() !== '' ? `${commentaire}\n${ligne}` : ligne;
}

/**
 * Date du dernier appel consigné, ou null si le client n'a jamais été appelé.
 *
 * Sert à faire remonter en premier ceux qu'on n'a pas encore tentés : sans
 * cela, les numéros qui ne décrochent jamais reviendraient en tête à chaque
 * ouverture, et les autres ne seraient jamais appelés.
 */
export function dernierAppel(commentaire: string | null): string | null {
  if (!commentaire) return null;
  let plusRecent: string | null = null;
  for (const ligne of commentaire.split('\n')) {
    if (!ligne.includes(MARQUE_APPEL)) continue;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(ligne.trim());
    if (!m?.[1] || !m[2] || !m[3]) continue;
    const iso = `${m[3]}-${m[2]}-${m[1]}`;
    if (!plusRecent || iso > plusRecent) plusRecent = iso;
  }
  return plusRecent;
}
